import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse, faXmark } from '@fortawesome/free-solid-svg-icons';

/**
 * Editable list of template lines, each `{ stock, quantity }`. Articles are
 * restricted to crew-flagged stock (the same `stocks` rows a location uses,
 * filtered to `crew === true`). The available stock is shown for orientation
 * but is NOT enforced here — overbooking is flagged on the delivery itself
 * after the template is applied, exactly as for manual entry.
 */
const TemplateBuilder = ({
  draft = [],
  crewStocks = [],
  onAddArticle,
  onRemoveArticle,
  onChangeQuantity,
}) => {
  const [justAddedId, setJustAddedId] = useState(null);

  const usedIds = useMemo(() => new Set(draft.map((d) => d.stock.id)), [draft]);
  const available = useMemo(
    () => crewStocks.filter((s) => !usedIds.has(s.id)),
    [crewStocks, usedIds]
  );

  const totalUnits = draft.reduce((sum, d) => sum + (d.quantity || 0), 0);

  const handleAdd = (e) => {
    const stockId = e.target.value;
    if (!stockId) return;
    const stock = crewStocks.find((s) => s.id === stockId);
    if (!stock) return;
    setJustAddedId(stockId);
    onAddArticle(stock);
    e.target.value = '';
  };

  return (
    <>
      {draft.length === 0 ? (
        <p className='text-muted mb-3'>
          Noch keine Artikel in der Vorlage. Unten Crew-Artikel hinzufügen.
        </p>
      ) : (
        <table className='table table-sm align-middle mb-2'>
          <tbody>
            {draft.map((d) => {
              const overStock = d.quantity > d.stock.quantity_available;
              return (
                <tr key={d.stock.id}>
                  <td>{d.stock.article_name}</td>
                  <td
                    className={`text-nowrap small ${
                      overStock ? 'text-danger fw-bold' : 'text-muted'
                    }`}
                  >
                    {d.stock.quantity_available}
                    <FontAwesomeIcon
                      icon={faWarehouse}
                      className='ms-1'
                      title='verfügbarer Bestand'
                    />
                  </td>
                  <td style={{ width: '7rem' }}>
                    <input
                      className={`form-control form-control-sm ${
                        overStock ? 'border-danger' : ''
                      }`}
                      type='number'
                      min='0'
                      autoFocus={d.stock.id === justAddedId}
                      value={d.quantity}
                      onChange={(e) =>
                        onChangeQuantity(
                          d.stock.id,
                          parseInt(e.target.value, 10) || 0
                        )
                      }
                    />
                  </td>
                  <td className='text-end' style={{ width: '2.5rem' }}>
                    <button
                      type='button'
                      className='btn btn-sm btn-link text-secondary p-0'
                      title='Artikel entfernen'
                      onClick={() => onRemoveArticle(d.stock.id)}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <select
        className='form-select form-select-sm'
        value=''
        disabled={available.length === 0}
        onChange={handleAdd}
      >
        <option value='' disabled>
          {crewStocks.length === 0
            ? 'Keine Crew-Artikel im Bestand'
            : available.length === 0
              ? 'Alle Crew-Artikel hinzugefügt'
              : '+ Crew-Artikel hinzufügen…'}
        </option>
        {available.map((s) => (
          <option key={s.id} value={s.id}>
            {s.article_name}
          </option>
        ))}
      </select>

      {draft.length > 0 && (
        <div className='text-muted small mt-2'>
          {draft.length} Positionen · {totalUnits} Einheiten
        </div>
      )}
    </>
  );
};

export default TemplateBuilder;
